import { Flex, type IconProps, type SpaceProps } from "@chakra-ui/react";
import { Section } from "components/section";
import { Soc2Badge, GdprBadge, HipaaBadge, CcpaBadge } from "components/security-badges";
import { type Theme } from "contexts/color-theme";
import React from "react";
import { remCalc } from "theme/utils";
import { TextTitle } from "ui/text";

type SecurityFeaturesProps = {
  /**
   * The color theme to use for text and badge colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * Use white badge backgrounds in the default/`light` theme.
   * The `dark` theme will always use white backgrounds.
   */
  hasWhiteBadges?: boolean;

  /**
   * Sets a custom title for the section.
   */
  title?: string;

  /**
   * Sets a custom description for the section.
   */
  description?: string;
} & SpaceProps;

const accentColor: IconProps["fill"] = "new.gray.150";
const badgeProps = {
  boxSize: { base: "69px", sm: "96px", md: "112px" },
  flex: 1,
  foregroundColor: "new.gray.950",
  maxW: { base: "100%", sm: "96px", md: "112px" },
};

export function SecurityFeatures({
  theme,
  title = "Hightouch doesn’t store your data",
  description = "Composable CDPs are more secure because no duplicate storage is required. Instead, we simply read from your data warehouse, where it stays safe and sound.",
  hasWhiteBadges,
  ...spaceProps
}: SecurityFeaturesProps) {
  const bgColor = hasWhiteBadges ?? theme === "dark" ? "white" : "new.gray.50";

  return (
    <Section {...spaceProps}>
      <Flex flexDir="column" gap="biggest">
        {(title || description) && (
          <TextTitle
            maxW={remCalc(740)}
            theme={theme}
            size="sm"
            align="center"
            title={title}
            description={description}
          />
        )}

        <Flex mx={{ md: "auto" }} gap={{ base: "small", md: "bigger" }}>
          <Soc2Badge backgroundColor={bgColor} {...badgeProps} />
          <GdprBadge backgroundColor={bgColor} {...badgeProps} />
          <HipaaBadge backgroundColor={bgColor} {...badgeProps} />
          <CcpaBadge backgroundColor={bgColor} accentColor={accentColor} {...badgeProps} />
        </Flex>
      </Flex>
    </Section>
  );
}
